import React from 'react';
import classes from './PostCardMediaPreviewTikTok.module.scss';
import { PostCardPreviewMediaDataType } from '../PostCardMediaPreview/PostCardMediaPreview.view';
import { serializeText } from '../../TextEditor/helpers/serializer';
import classNames from 'classnames';
import { Network, PostMultiMediaAttachMode, PostTikTokPrivacy, SocialOAuthProvider } from '../../../../models/graphql';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import { SocialProfileWithBoardsAndMentionType } from '../../helpers/mapSelectedProfilesValues';
import { Badge } from '../../../../components/containers/Badge';
import { BadgeColorsEnum, BadgeSizesEnum } from '../../../../constants/badge';
import { TikTokProfileValues } from '../../../../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { InvalidMediaSet } from '../PostCardMediaPreview/InvalidMediaSet';
import { SingularValidator } from '../../validators/helpers/singular-validator';
import { MediaTypePostEditorState } from '../../../../models/post-editor';

export type PostCardMediaPreviewTikTokProfile = {
  socialProfile: SocialProfileWithBoardsAndMentionType & { __typename: 'TikTokSocialProfile' };
  privacy: PostTikTokPrivacy | null;
  allowedActions: TikTokProfileValues['allowedActions'];
};

export type PostCardMediaPreviewTikTokViewProps = PostCardPreviewMediaDataType & PostCardMediaPreviewTikTokProfile;

export const PerfectTikTokRatioEdge = 1 / 1.73; // 0.5780
export const FixedTikTokRatio = 1 / 1.95; // 0.5128

export const isInvalidTikTokMedia = (media: MediaTypePostEditorState[]): boolean =>
  SingularValidator.noImagesAllowedIvalid(media) ||
  SingularValidator.noGifsAllowedInvalid(media) ||
  SingularValidator.noMixedMediaInvalid(media, PostMultiMediaAttachMode.MULTIPLE) ||
  SingularValidator.tikTokMediaLimitInvalid(media, PostMultiMediaAttachMode.MULTIPLE);

export const PostCardMediaPreviewTikTokView = (props: PostCardMediaPreviewTikTokViewProps): JSX.Element => {
  const { text, media, socialProfile, privacy } = props;
  const serializedText = serializeText(text);

  let privacyBadge: string;

  switch (privacy) {
    case PostTikTokPrivacy.SELF_ONLY:
      privacyBadge = 'Private';
      break;
    case PostTikTokPrivacy.MUTUAL_FOLLOW_FRIENDS:
      privacyBadge = 'Friends';
      break;
    case PostTikTokPrivacy.FOLLOWER_OF_CREATOR:
      privacyBadge = 'Followers only';
      break;
    case null:
    case PostTikTokPrivacy.ASK:
    case PostTikTokPrivacy.PUBLIC_TO_EVERYONE:
      privacyBadge = '';
      break;
  }

  let objectFit: 'Cover' | 'Contain' = 'Cover';
  const imageAspectRatio = media.length ? media[0].width / media[0].height : FixedTikTokRatio;
  if (imageAspectRatio > PerfectTikTokRatioEdge) {
    objectFit = 'Contain';
  }

  const noMediaBlock = (
    <div className={classNames(classes.NoContent, 'grid-x', 'align-middle', 'align-center')}>
      <p>You need to add a video for TikTok</p>
    </div>
  );

  const invalidMediaBlock = <InvalidMediaSet className={classes.NoContent} />;

  return (
    <>
      <div className={classes.PostCardMediaPreviewTikTok}>
        <div className={classNames(classes.TikTokPostContentWrapper, classes[`TikTokPostContentWrapper${objectFit}`])}>
          {isInvalidTikTokMedia(media) ? (
            invalidMediaBlock
          ) : media.length ? (
            <>
              {/* in Case we added video to the ML and we still haven't finsihed transcoding, we won't have media.url. IN that case we show media thumnail (image) */}
              {media[0].url ? <video src={media[0].url} /> : <img src={media[0].thumbnailUrl} alt={'video cover'} />}
              <div className={classes.PlayButtonWrapper}>
                <Icon name={IconsEnum.play} size={'large'} />
              </div>
            </>
          ) : (
            noMediaBlock
          )}
        </div>
        <div className={classes.BackgroundGradient} />
        <div className={classes.SocialProfileWrapper}>
          <div className={classes.SocialProfileIconsWrapper}>
            <div className={classes.Icon}>
              <Icon name={IconsEnum.likes} size={'large'} />{' '}
            </div>
            <div className={classes.Icon}>
              <Icon name={IconsEnum.comments} size={'large'} />{' '}
            </div>
            <div className={classes.Icon}>
              <Icon name={IconsEnum.igSave} size={'large'} />{' '}
            </div>
            <div className={classes.Icon}>
              <Icon name={IconsEnum.share} size={'large'} />{' '}
            </div>
          </div>
          <SocialProfile
            socialProfile={{
              id: socialProfile.id,
              provider: SocialOAuthProvider.TIKTOK,
              network: Network.TIKTOK,
              name: socialProfile.displayName,
              type: ''
            }}
            disconnected={!socialProfile.connected}
            size={'medium'}
            image={socialProfile.photoUrl}
          />
        </div>
        <div className={classes.ContentWraper}>
          {privacyBadge ? (
            <div className={classNames('margin-bottom-small-16', classes.BadgeWrapper)}>
              <Badge color={BadgeColorsEnum.DARK} size={BadgeSizesEnum.LARGE} text={privacyBadge} />
            </div>
          ) : null}
          <h4 className={classes.ProfileName}>{socialProfile.displayName}</h4>
          {serializedText ? <div className={classes.TikTokText}>{serializedText}</div> : null}
        </div>
      </div>
    </>
  );
};
